// Team Pulse — admin-only per-dealmaker weekly activity metrics.
//
// Reads GET /api/team/activity/weekly (permission: view_team_activity), which
// serves the derived dealmaker_activity_weekly table plus server-computed
// anomaly flags. Metric definitions and attribution rules live in
// backend/scripts/derive_dealmaker_activity.py.
//
// Coverage honesty is the whole point of this view's rendering rules: weeks
// before a member's integration produced its first data are drawn as "no
// coverage" (hollow ticks), never as zero activity — a zero only counts
// against someone when the pipe was actually connected. The current week is
// partial and drawn dimmed; deltas and flags use full weeks only.

// hint = one-line tooltip on the column header.
const TP_METRICS = [
  { key: 'emails_sent',       label: 'Outbound emails',   cov: 'emails_sent',
    hint: 'External emails sent, by true send date' },
  { key: 'email_domains',     label: 'Companies reached', cov: 'emails_sent',
    hint: 'Distinct external domains emailed' },
  { key: 'meetings_recorded', label: 'Recorded meetings', cov: 'meetings_recorded',
    hint: 'Fireflies-recorded meetings (host-attributed)' },
  { key: 'meetings_calendar', label: 'External meetings', cov: 'meetings_calendar',
    hint: 'Calendar events with an outside attendee · all-day events excluded' },
  { key: 'notes_logged',      label: 'Notes logged',      cov: 'notes_logged',
    hint: 'Self-logged CRM notes (DealCloud + Atlas)' },
  { key: 'ds_calls',          label: 'D/S calls',         cov: null,
    hint: 'Intro calls credited by contact owner — intake-scheduled lead feed, not personal effort' },
  { key: 'marketing_replies', label: 'Marketing replies', cov: null,
    hint: 'Sellers entering Responded, credited to the company owner · data starts Aug 2026' },
  { key: 'marketing_calls_scheduled', label: 'Mkt calls scheduled', cov: null,
    hint: 'Marketing contacts reaching Call Scheduled · data starts Aug 2026' },
  { key: 'marketing_calls_held', label: 'Mkt calls held',  cov: null,
    hint: 'Marketing contacts reaching DS Call Held · data starts Aug 2026' },
  { key: 'marketing_dispositions', label: 'Dispositions',  cov: null,
    hint: 'Marketing exits this person recorded · the discipline metric' },
];

const TP_FLAG_LABEL = {
  sharp_drop: (f) => `${tpMetricLabel(f.metric)} <50% of 8wk median`,
  streak_decline: (f) => `${tpMetricLabel(f.metric)} down ${f.weeks_declining}wk straight`,
};

const tpMetricLabel = (key) => (TP_METRICS.find((m) => m.key === key)?.label || key).toLowerCase();

// Tiny bar sparkline. Pre-coverage weeks render as hollow baseline ticks; the
// trailing (partial) week is dimmed. Hover any bar for "week of … — N".
// `onBarClick(i)` (optional) makes covered bars clickable — Team Pulse opens
// the drill-down drawer; the offboarding report passes nothing and stays
// read-only.
const TPSpark = ({ vals, weeks, coverageIso, partial = true, onBarClick }) => {
  const n = vals.length;
  const bw = 7, gap = 2, h = 30;
  const W = n * (bw + gap) - gap;
  const max = Math.max(1, ...vals);
  const covIdx = coverageIso ? weeks.findIndex((w) => w >= coverageIso) : 0;
  const noCov = (i) => covIdx === -1 || i < covIdx;
  return (
    <svg width={W} height={h} style={{ display: 'block' }} aria-hidden="true">
      {vals.map((v, i) => {
        const x = i * (bw + gap);
        if (noCov(i)) {
          return <rect key={i} x={x} y={h - 2} width={bw} height={2} rx={1}
            fill="var(--border)">
            <title>{`week of ${weeks[i]} — no coverage`}</title>
          </rect>;
        }
        const bh = v === 0 ? 2 : Math.max(3, Math.round((v / max) * (h - 4)));
        const isPartial = partial && i === n - 1;
        const clickable = !!onBarClick && v > 0;
        return <g key={i}>
          <rect x={x} y={h - bh} width={bw} height={bh} rx={1.5}
            fill={v === 0 ? 'var(--border-strong)' : (HUE.blue.dot)}
            opacity={isPartial ? 0.4 : (v === 0 ? 0.9 : 0.85)}>
            <title>{`week of ${weeks[i]} — ${v}${isPartial ? ' (week in progress)' : ''}${clickable ? ' · click for detail' : ''}`}</title>
          </rect>
          {clickable && (
            <rect x={x - 1} y={0} width={bw + 2} height={h} fill="transparent"
              style={{ cursor: 'pointer' }} onClick={() => onBarClick(i)}>
              <title>{`week of ${weeks[i]} — ${v} · click for detail`}</title>
            </rect>
          )}
        </g>;
      })}
    </svg>
  );
};

// Drill-down drawer: the actual rows behind one member × week × metric bar.
const TPDrill = ({ drill, onClose }) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    if (!drill) return;
    let on = true;
    setData(null); setErr('');
    window.AtlasApi.teamPulseDetail({
      member_id: drill.memberId, week: drill.week, metric: drill.metric,
    })
      .then((d) => { if (on) setData(d); })
      .catch((e) => { if (on) setErr((e && (e.message || e.detail)) || 'Failed to load'); });
    return () => { on = false; };
  }, [drill]);
  if (!drill) return null;
  const meta = TP_METRICS.find((m) => m.key === drill.metric);
  const label = meta?.label || drill.metric;
  return (
    <window.Drawer open onClose={onClose} width={520}
      title={`${label} — week of ${window.fmtDate(drill.week)}`}
      subtitle={`${drill.memberName} · ${drill.count} total`}>
      {err && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--rose-fg)' }}>{err}</div>}
      {!data && !err && <div style={{ padding: 14 }}><window.SkeletonRows rows={4} /></div>}
      {data && (
        <div style={{ padding: '6px 0' }}>
          {data.items.length === 0 && (
            <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-3)' }}>
              Nothing found
            </div>
          )}
          {data.items.map((it) => (
            <div key={it.id} style={{ padding: '8px 14px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                {data.kind === 'engagement' ? (
                  <span onClick={(e) => { if (!(e.metaKey || e.ctrlKey)) onClose(); window.AtlasNav?.goToDeal?.(it.id); }}
                    style={{ fontSize: 12.5, color: 'var(--accent-text)', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.title || '(untitled)'}
                  </span>
                ) : (
                  <span style={{ fontSize: 12.5, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.title || ''}>
                    {it.title || '(untitled)'}
                  </span>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{window.fmtDate(it.ts)}</span>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 2, fontSize: 11.5 }}>
                {it.company_id ? (
                  <span onClick={(e) => { if (!(e.metaKey || e.ctrlKey)) onClose(); window.AtlasNav?.goToCompany?.(it.company_id); }}
                    style={{ color: 'var(--accent-text)', cursor: 'pointer' }}>
                    {it.company_name || 'company'}
                  </span>
                ) : <span style={{ color: 'var(--text-4)' }}>{it.company_name || 'no company link'}</span>}
                {it.context && <span style={{ color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.context}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </window.Drawer>
  );
};

// Last full week's value + delta vs the week before it.
// The number is the LAST COMPLETED week (the in-progress week is excluded so
// mid-week reads aren't artificially low) — bars further back can be nonzero
// while this shows 0. Labeled "last wk" so that never reads as a mismatch.
const TPStat = ({ vals, weekIso }) => {
  const last = vals.length >= 2 ? vals[vals.length - 2] : null;
  const prev = vals.length >= 3 ? vals[vals.length - 3] : null;
  const delta = (last != null && prev != null) ? last - prev : null;
  const tone = delta == null || delta === 0 ? 'var(--text-3)'
    : delta > 0 ? HUE.green.dot : HUE.rose.dot;
  const tip = weekIso
    ? `${last ?? '—'} in the last completed week (${window.fmtDate(weekIso)})`
      + (delta != null && delta !== 0 ? ` · ${delta > 0 ? '+' : ''}${delta} vs the week before` : '')
    : undefined;
  return (
    <div style={{ minWidth: 52 }} title={tip}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span style={{ fontSize: 15, fontWeight: 650, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
          {last ?? '—'}
        </span>
        {delta != null && delta !== 0 && (
          <span style={{ fontSize: 10.5, fontWeight: 600, color: tone, fontVariantNumeric: 'tabular-nums' }}>
            {delta > 0 ? '▲' : '▼'}{Math.abs(delta)}
          </span>
        )}
      </div>
      <div style={{ fontSize: 9, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)', marginTop: 1 }}>
        last wk
      </div>
    </div>
  );
};

// Render the brief's Slack-flavored markdown safely: escape everything, then
// re-introduce *bold* and _muted_ (server-generated content only).
const tpBriefHtml = (md) => {
  const esc = (md || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc
    .replace(/\*([^*\n]+)\*/g, '<b>$1</b>')
    .replace(/_([^_\n]+)_/g, '<span style="color:var(--text-4)">$1</span>')
    .replace(/\n/g, '<br/>');
};

// Latest weekly brief — generated by the worker every Monday; the same text
// that goes to Slack (when SLACK_BRIEF_WEBHOOK_URL is configured).
const TPBrief = () => {
  const [brief, setBrief] = React.useState(undefined); // undefined=loading, null=none
  React.useEffect(() => {
    let on = true;
    window.AtlasApi.teamPulseBriefs()
      .then((d) => { if (on) setBrief((d.briefs || [])[0] || null); })
      .catch(() => { if (on) setBrief(null); });
    return () => { on = false; };
  }, []);
  if (!brief) return null;
  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)',
      padding: '12px 14px', marginBottom: 14, fontSize: 12.5, lineHeight: 1.6,
      color: 'var(--text-2)',
    }}>
      <div dangerouslySetInnerHTML={{ __html: tpBriefHtml(brief.body_md) }} />
      <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 6 }}>
        Weekly brief · generated {window.fmtDate(brief.created_at)}
        {brief.posted_at ? ' · posted to Slack' : ''}
      </div>
    </div>
  );
};

// Weekly / Daily switch shared by Team pulse and Daily reports — together
// they are the "Team activity" admin page (one sidebar row); each tab keeps
// its own view key so both old deep links still resolve.
const TeamActivitySwitch = ({ tab, onTab }) => (
  <div style={{ display: 'inline-flex', gap: 2, padding: 2, borderRadius: 6, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
    {[['weekly', 'Weekly', 'team-pulse'], ['daily', 'Daily', 'daily-reports']].map(([k, label, viewKey]) => (
      <Btn key={k} size="xs" active={tab === k} onClick={() => tab !== k && onTab(viewKey)}>{label}</Btn>
    ))}
  </div>
);
window.TeamActivitySwitch = TeamActivitySwitch;

const TeamPulseView = ({ switcher = null } = {}) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [weeksN, setWeeksN] = React.useState(12);
  const [drill, setDrill] = React.useState(null);

  React.useEffect(() => {
    let on = true;
    setData(null); setErr(null);
    window.AtlasApi.teamPulse({ weeks: weeksN })
      .then((d) => { if (on) setData(d); })
      .catch((e) => { if (on) setErr(e?.message || 'Failed to load'); });
    return () => { on = false; };
  }, [weeksN]);

  return (
    // The app's main pane is overflow:hidden — each view owns its scrolling.
    <div style={{ height: '100%', overflowY: 'auto' }}>
    <div style={{ padding: '18px 22px', maxWidth: 1180 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 2 }}>
        <h1 style={{ fontSize: 17, fontWeight: 650, margin: 0, color: 'var(--text)' }}>{switcher ? 'Team activity' : 'Team pulse'}</h1>
        {switcher}
        <div style={{ flex: 1 }} />
        {[8, 12, 26].map((n) => (
          <Btn key={n} size="sm" active={weeksN === n} onClick={() => setWeeksN(n)}>{n}w</Btn>
        ))}
      </div>

      <TPBrief />

      {err && <div style={{ fontSize: 12.5, color: HUE.rose.dot, padding: '12px 0' }}>{String(err)}</div>}
      {!data && !err && <div style={{ fontSize: 12.5, color: 'var(--text-3)', padding: '12px 0' }}>Loading…</div>}

      {data && data.members.map((m) => (
        <div key={m.id} style={{
          border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)',
          padding: '12px 14px', marginBottom: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span
              onClick={() => window.AtlasNav?.goToUser?.(m.id, m.name)}
              style={{ fontSize: 13, fontWeight: 650, color: 'var(--text)', cursor: 'pointer' }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >{m.name}</span>
            {m.team_name && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{m.team_name}</span>}
            <div style={{ flex: 1 }} />
            {m.flags.map((f, i) => (
              <Pill key={i} hue={f.type === 'sharp_drop' ? 'rose' : 'amber'} sz="sm" dot
                title={`Flagged week of ${f.week}`}>
                {(TP_FLAG_LABEL[f.type] || (() => f.type))(f)}
              </Pill>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 22, overflowX: 'auto', paddingBottom: 2 }}>
            {TP_METRICS.map(({ key, label, cov, hint }) => (
              <div key={key} style={{ flexShrink: 0 }} title={hint}>
                {/* Dotted underline = "hover me" affordance for the hint. */}
                <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em',
                  textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 5,
                  textDecoration: 'underline dotted var(--border-strong)', textUnderlineOffset: 3,
                  cursor: 'help', width: 'fit-content' }}>
                  {label}
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                  <TPSpark vals={m.metrics[key]} weeks={data.weeks}
                    coverageIso={cov ? m.coverage[cov] : null}
                    onBarClick={(i) => setDrill({
                      memberId: m.id, memberName: m.name, metric: key,
                      week: data.weeks[i], count: m.metrics[key][i],
                    })} />
                  <TPStat vals={m.metrics[key]}
                    weekIso={data.weeks.length >= 2 ? data.weeks[data.weeks.length - 2] : null} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {data && data.members.length === 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No active dealmakers found.</div>
      )}

      <TPDrill drill={drill} onClose={() => setDrill(null)} />
    </div>
    </div>
  );
};

window.TeamPulseView = TeamPulseView;
// Reused by the offboarding report on the user profile.
window.TPSpark = TPSpark;
